/**
 * tickerNames — how the game ticker and game card shorten team / player names
 * ("full" | "short" | "abbreviated", the user's ticker preference).
 * Moved out of components/gameTickerParts.jsx so it can be tested.
 */

const COLLEGE_RE = /\b(ncaa|ncaaf|ncaab|ncaaw|college|cfb|cbb)\b/i;
export const isCollegeLeague = (league) => COLLEGE_RE.test(String(league || '').replace(/[_-]/g, ' '));

// A last word that names no team on its own.
const GENERIC_TAILS = new Set(['state', 'st', 'st.', 'tech', 'a&m', 'college', 'university', 'u']);
export const hasGenericTail = (name) => {
  const words = String(name || '').trim().split(/\s+/);
  return words.length >= 2 && GENERIC_TAILS.has(words[words.length - 1].toLowerCase());
};

/** "Ohio State" → "Ohio St.", "South Florida" stays; never cut to one word. */
export const compactCollegeName = (name) => String(name || '').trim().replace(/\bState\b/g, 'St.');

export const formatTickerName = (fullName, format, isTeam = true, league = '') => {
  if (!fullName || fullName === 'TBD') return fullName;

  const name = fullName.trim();
  // College names carry no mascot ("Ohio State", "South Florida"), so the
  // pro-style "last word" rule gave "State @ State" (audit Oct 26).
  if (isTeam && format !== 'full' && (isCollegeLeague(league) || hasGenericTail(name))) {
    return compactCollegeName(name);
  }
  
  switch (format) {
    case 'full':
      // Return full name as-is
      return name;
      
    case 'abbreviated':
      if (isTeam) {
        // For teams: Try to get standard abbreviation or create one
        // Common team abbreviations
        const teamAbbreviations = {
          'cleveland cavaliers': 'CLE', 'golden state warriors': 'GSW', 'los angeles lakers': 'LAL',
          'los angeles clippers': 'LAC', 'boston celtics': 'BOS', 'miami heat': 'MIA',
          'milwaukee bucks': 'MIL', 'phoenix suns': 'PHX', 'denver nuggets': 'DEN',
          'minnesota timberwolves': 'MIN', 'dallas mavericks': 'DAL', 'oklahoma city thunder': 'OKC',
          'new york knicks': 'NYK', 'brooklyn nets': 'BKN', 'philadelphia 76ers': 'PHI',
          'chicago bulls': 'CHI', 'toronto raptors': 'TOR', 'indiana pacers': 'IND',
          'detroit pistons': 'DET', 'atlanta hawks': 'ATL', 'charlotte hornets': 'CHA',
          'washington wizards': 'WAS', 'orlando magic': 'ORL', 'memphis grizzlies': 'MEM',
          'new orleans pelicans': 'NOP', 'houston rockets': 'HOU', 'san antonio spurs': 'SAS',
          'utah jazz': 'UTA', 'sacramento kings': 'SAC', 'portland trail blazers': 'POR',
          // NFL
          'kansas city chiefs': 'KC', 'buffalo bills': 'BUF', 'san francisco 49ers': 'SF',
          'dallas cowboys': 'DAL', 'philadelphia eagles': 'PHI', 'green bay packers': 'GB',
          'new england patriots': 'NE', 'baltimore ravens': 'BAL', 'cincinnati bengals': 'CIN',
          'pittsburgh steelers': 'PIT', 'los angeles rams': 'LAR', 'los angeles chargers': 'LAC',
          'seattle seahawks': 'SEA', 'new york giants': 'NYG', 'new york jets': 'NYJ',
          'denver broncos': 'DEN', 'las vegas raiders': 'LV', 'arizona cardinals': 'ARI',
          'detroit lions': 'DET', 'chicago bears': 'CHI', 'minnesota vikings': 'MIN',
          'tampa bay buccaneers': 'TB', 'miami dolphins': 'MIA', 'cleveland browns': 'CLE',
          // MLB
          'new york yankees': 'NYY', 'new york mets': 'NYM', 'los angeles dodgers': 'LAD',
          'boston red sox': 'BOS', 'chicago cubs': 'CHC', 'chicago white sox': 'CWS',
          // NHL
          'vegas golden knights': 'VGK', 'colorado avalanche': 'COL', 'tampa bay lightning': 'TBL',
        };
        const lowerName = name.toLowerCase();
        if (teamAbbreviations[lowerName]) return teamAbbreviations[lowerName];
        // Fallback: take first 3 letters of last word
        const words = name.split(' ');
        return words[words.length - 1].substring(0, 3).toUpperCase();
      } else {
        // For players: "Carlos Alcaraz" -> "C. Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
        }
        return name;
      }
      
    case 'short':
    default:
      if (isTeam) {
        // For teams: Remove city name, keep team name
        // "Cleveland Cavaliers" -> "Cavaliers", "Los Angeles Lakers" -> "Lakers"
        const words = name.split(' ');
        if (words.length >= 2) {
          // Common two-word team names to keep together
          const twoWordTeams = ['trail blazers', 'red sox', 'white sox', 'blue jays', 'golden knights', '76ers'];
          const lastTwo = words.slice(-2).join(' ').toLowerCase();
          if (twoWordTeams.includes(lastTwo)) {
            return words.slice(-2).join(' ');
          }
          return words[words.length - 1];
        }
        return name;
      } else {
        // For players: Just last name "Carlos Alcaraz" -> "Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return parts[parts.length - 1];
        }
        return name;
      }
  }
};
